"use client";

import { useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { formatNumber } from "@/lib/utils";
import { Panel, type Stat, StatStrip } from "../../primitives";
import type { SceneProps } from "../../types";
import { SCENE_SLOWDOWN, scaleDelay } from "../../timing";
import { useSceneFinish } from "../../use-scene-finish";
import { JOURNEY_ORDER, TIMELINE } from "./data";
import { MemberPanel } from "./member-panel";
import { type Status, Thread } from "./thread";

const fmtNum = (v: number) => formatNumber(v, { precision: 0 });
const fmtMoney = (v: number) => `$${formatNumber(v, { precision: 0 })}`;

export function MembershipJourney({ reduce, paused = false, onComplete }: SceneProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { amount: 0.3 });
  const [visible, setVisible] = useState(0);

  const complete = visible >= TIMELINE.length;

  // Hold on the completed journey, then advance to the next use case.
  useSceneFinish({
    active: complete && inView && !paused,
    delay: scaleDelay(2200, SCENE_SLOWDOWN),
    enabled: !reduce,
    onComplete,
  });

  // Reveal the thread one item at a time while in view; pause off-screen, when
  // explicitly paused, and freeze once the journey completes.
  useEffect(() => {
    if (reduce || paused || !inView || complete) return;
    const base = visible === 0 ? 450 : (TIMELINE[visible - 1]?.delay ?? 1000);
    const id = setTimeout(() => setVisible((v) => v + 1), scaleDelay(base, SCENE_SLOWDOWN));
    return () => clearTimeout(id);
  }, [inView, visible, reduce, paused, complete]);

  const effVisible = reduce ? TIMELINE.length : visible;

  // Active journey milestone = the furthest step reached by revealed items.
  let activeIndex = 0;
  for (const item of TIMELINE.slice(0, effVisible)) {
    if ("step" in item && item.step) {
      activeIndex = Math.max(activeIndex, JOURNEY_ORDER.indexOf(item.step));
    }
  }
  const lapseReached = activeIndex >= JOURNEY_ORDER.indexOf("lapse");
  const returned = activeIndex >= JOURNEY_ORDER.indexOf("returned");
  const unlimited = activeIndex >= JOURNEY_ORDER.length - 1;

  // Once every item has been revealed the journey is fully done — push the
  // stepper one past the last milestone so the final step reads completed
  // (green check) rather than active.
  const finished = effVisible >= TIMELINE.length;
  const stepperIndex = finished ? JOURNEY_ORDER.length : activeIndex;

  const status: Status = unlimited
    ? "unlimited"
    : returned
      ? "reengaged"
      : lapseReached
        ? "atrisk"
        : "active";

  // The rollup stats tick up as this member hits the matching milestones —
  // making the spotlight feel like one contribution to the totals.
  const reEngaged = 21 + (returned ? 1 : 0);
  const upgraded = 12 + (unlimited ? 1 : 0);
  const recovered = 4860 + (returned ? 120 : 0) + (unlimited ? 40 : 0);

  const stats: Stat[] = [
    { name: "Churn risk", value: 34, formatter: fmtNum },
    { name: "Re-engaged", value: reEngaged, formatter: fmtNum },
    { name: "Upgraded", value: upgraded, formatter: fmtNum },
    { name: "Recovered", value: recovered, formatter: fmtMoney },
  ];

  return (
    <div ref={rootRef} className="flex h-[36rem] flex-col p-4 sm:p-5 lg:h-full">
      {/* Top level: the playbook + animated rollup stats */}
      <div className="flex flex-col gap-4 border-b border-border pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">Churn risk → Unlimited</h3>
          <p className="text-sm text-muted-foreground">Make sure members stay engaged.</p>
        </div>
        <StatStrip stats={stats} />
      </div>

      {/* One instance: a single member's journey. The identity + journey rail is
          desktop enrichment; mobile leads with the conversation itself. */}
      <div className="flex min-h-0 flex-1 flex-col pt-4 lg:grid lg:grid-cols-[300px_1fr] lg:gap-5">
        <div className="hidden min-h-0 flex-col gap-1.5 overflow-y-auto lg:flex [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <MemberPanel
            activeIndex={stepperIndex}
            unlimited={unlimited}
            returned={returned}
            reduce={reduce}
          />
        </div>

        <Panel className="min-h-0 flex-1 overflow-hidden">
          <Thread items={TIMELINE} visible={effVisible} status={status} reduce={reduce} />
        </Panel>
      </div>
    </div>
  );
}
